import { detectFaces } from "../engine/faceDetection";

export type BodyReacquireStatus = "tracking" | "freeze" | "searching";

export type BodyTrackingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type BodyTrackingState = {
  elementId: string;
  trackedBox: BodyTrackingBox | null;
  confidence: number;
  smoothedTrajectory: {
    x: number;
    y: number;
    vx: number;
    vy: number;
  };
  reacquireStatus: BodyReacquireStatus;
  deterministicFallback: boolean;
  lastUpdatedMs: number;
};

type InternalBodyTrackingState = BodyTrackingState & {
  missCount: number;
  reacquireRadius: number;
};

type TickArgs = {
  videoMap: Map<string, HTMLVideoElement>;
  targetElementIds: string[];
  nowMs?: number;
};

const DETECT_INTERVAL_MS = 120;
const DETECTOR_FAIL_COOLDOWN_MS = 2500;
const FREEZE_MISS_LIMIT = 6;
const SEARCH_MISS_LIMIT = 18;
const CENTER_SMOOTHING = 0.34;
const VELOCITY_CLAMP = 0.14;

export class FxBodyTracker {
  private states = new Map<string, InternalBodyTrackingState>();
  private detectRunning = false;
  private lastDetectMs = 0;
  private disabledUntilMs = 0;
  private roundRobinCursor = 0;

  tick(args: TickArgs): void {
    const { videoMap, targetElementIds } = args;
    const nowMs = args.nowMs ?? now();
    const targets = dedupeIds(targetElementIds);
    const targetSet = new Set(targets);

    this.pruneInactiveTargets(targetSet);
    if (targets.length === 0) return;
    if (this.detectRunning) return;
    if (nowMs < this.disabledUntilMs) return;
    if (nowMs - this.lastDetectMs < DETECT_INTERVAL_MS) return;

    const candidate = this.pickNextCandidate(targets, videoMap);
    if (!candidate) {
      for (const elementId of targets) this.applyMiss(elementId, nowMs);
      return;
    }

    this.lastDetectMs = nowMs;
    this.detectRunning = true;
    void this
      .sampleCandidate(candidate.elementId, candidate.video, nowMs)
      .catch(() => undefined)
      .finally(() => {
        this.detectRunning = false;
      });
  }

  getState(elementId: string): BodyTrackingState | null {
    const state = this.states.get(elementId);
    if (!state) return null;
    return {
      elementId: state.elementId,
      trackedBox: state.trackedBox ? { ...state.trackedBox } : null,
      confidence: state.confidence,
      smoothedTrajectory: { ...state.smoothedTrajectory },
      reacquireStatus: state.reacquireStatus,
      deterministicFallback: state.deterministicFallback,
      lastUpdatedMs: state.lastUpdatedMs,
    };
  }

  reset(): void {
    this.states.clear();
    this.roundRobinCursor = 0;
  }

  dispose(): void {
    this.reset();
    this.detectRunning = false;
    this.disabledUntilMs = 0;
  }

  private async sampleCandidate(
    elementId: string,
    video: HTMLVideoElement,
    nowMs: number
  ): Promise<void> {
    try {
      const faces = await detectFaces(video, { maxFaces: 1, minConfidence: 0.4 });
      const first = faces[0];
      if (!first) {
        this.applyMiss(elementId, nowMs);
        return;
      }

      const safeW = Math.max(1, video.videoWidth || video.clientWidth || 1);
      const safeH = Math.max(1, video.videoHeight || video.clientHeight || 1);
      const box = first.box;

      const normX = clamp01(box.x / safeW);
      const normY = clamp01(box.y / safeH);
      const normW = clamp01(box.width / safeW);
      const normH = clamp01(box.height / safeH);
      const centerX = clamp01(normX + normW * 0.5);
      const centerY = clamp01(normY + normH * 0.5);
      const confidence = clamp01(Number.isFinite(box.confidence) ? box.confidence : 0.9);

      this.applyDetectedSubject(elementId, {
        x: normX,
        y: normY,
        width: normW,
        height: normH,
      }, centerX, centerY, confidence, nowMs);
    } catch {
      this.disabledUntilMs = nowMs + DETECTOR_FAIL_COOLDOWN_MS;
      this.applyMiss(elementId, nowMs);
    }
  }

  private applyDetectedSubject(
    elementId: string,
    trackedBox: BodyTrackingBox,
    rawCenterX: number,
    rawCenterY: number,
    confidence: number,
    nowMs: number
  ): void {
    const prev = this.states.get(elementId) ?? createDefaultState(elementId, nowMs);
    const nextX = lerp(prev.smoothedTrajectory.x, rawCenterX, CENTER_SMOOTHING);
    const nextY = lerp(prev.smoothedTrajectory.y, rawCenterY, CENTER_SMOOTHING);
    const vx = clamp(nextX - prev.smoothedTrajectory.x, -VELOCITY_CLAMP, VELOCITY_CLAMP);
    const vy = clamp(nextY - prev.smoothedTrajectory.y, -VELOCITY_CLAMP, VELOCITY_CLAMP);

    this.states.set(elementId, {
      elementId,
      trackedBox,
      confidence,
      smoothedTrajectory: { x: nextX, y: nextY, vx, vy },
      reacquireStatus: "tracking",
      deterministicFallback: false,
      lastUpdatedMs: nowMs,
      missCount: 0,
      reacquireRadius: 0.15,
    });
  }

  private applyMiss(elementId: string, nowMs: number): void {
    const prev = this.states.get(elementId) ?? createDefaultState(elementId, nowMs);
    const missCount = prev.missCount + 1;

    let status: BodyReacquireStatus = "freeze";
    let confidence = prev.confidence;
    let trackedBox = prev.trackedBox;
    let centerX = prev.smoothedTrajectory.x;
    let centerY = prev.smoothedTrajectory.y;
    let vx = prev.smoothedTrajectory.vx * 0.6;
    let vy = prev.smoothedTrajectory.vy * 0.6;
    let reacquireRadius = prev.reacquireRadius;

    if (missCount <= FREEZE_MISS_LIMIT) {
      status = "freeze";
      confidence = clamp01(confidence * 0.96);
    } else if (missCount <= SEARCH_MISS_LIMIT) {
      status = "searching";
      confidence = clamp01(confidence * 0.9);
      vx *= 0.4;
      vy *= 0.4;
      reacquireRadius = clamp01(reacquireRadius + 0.06);
    } else {
      status = "searching";
      confidence = 0;
      trackedBox = null;
      centerX = lerp(centerX, 0.5, 0.15);
      centerY = lerp(centerY, 0.5, 0.15);
      vx = 0;
      vy = 0;
      reacquireRadius = 1;
    }

    this.states.set(elementId, {
      elementId,
      trackedBox,
      confidence,
      smoothedTrajectory: { x: centerX, y: centerY, vx, vy },
      reacquireStatus: status,
      deterministicFallback: true,
      lastUpdatedMs: nowMs,
      missCount,
      reacquireRadius,
    });
  }

  private pickNextCandidate(
    targetElementIds: string[],
    videoMap: Map<string, HTMLVideoElement>
  ): { elementId: string; video: HTMLVideoElement } | null {
    const total = targetElementIds.length;
    if (total === 0) return null;

    for (let i = 0; i < total; i += 1) {
      const idx = (this.roundRobinCursor + i) % total;
      const elementId = targetElementIds[idx];
      const video = videoMap.get(elementId);
      if (!video) continue;
      if (video.readyState < 2) continue;
      if (video.videoWidth <= 0 || video.videoHeight <= 0) continue;
      this.roundRobinCursor = (idx + 1) % total;
      return { elementId, video };
    }

    this.roundRobinCursor = (this.roundRobinCursor + 1) % total;
    return null;
  }

  private pruneInactiveTargets(activeTargets: Set<string>): void {
    for (const elementId of this.states.keys()) {
      if (!activeTargets.has(elementId)) {
        this.states.delete(elementId);
      }
    }
  }
}

function createDefaultState(elementId: string, nowMs: number): InternalBodyTrackingState {
  return {
    elementId,
    trackedBox: null,
    confidence: 0,
    smoothedTrajectory: { x: 0.5, y: 0.5, vx: 0, vy: 0 },
    reacquireStatus: "searching",
    deterministicFallback: true,
    lastUpdatedMs: nowMs,
    missCount: 0,
    reacquireRadius: 0.15,
  };
}

function now(): number {
  if (typeof performance !== "undefined") return performance.now();
  return Date.now();
}

function dedupeIds(input: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of input) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp01(t);
}
